import React from 'react';
import {
  AppBar,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { V4_DIALOG_CONTENT, V4_DIALOG_MD, V4_ICON_BUTTON, V4_SMALL_SCREEN } from '../../theme/v4Compat';
import { notifications } from '../../utils';
import { AnimalTypeSelect, LocationPositionSelect, LocationStableSelect } from '../../elements';
import Alert from '@mui/material/Alert';
import { LocationPosition, LocationStable } from '../../models/locations';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { AnimalType } from '../../models/animal';
import { HealthSystemDomainLocationsResultLocationViewModel } from '../../api/HealthSystemApi';

interface LocationUpdateModalProps {
  location: HealthSystemDomainLocationsResultLocationViewModel;
  opened: boolean;
  onLocationUpdated: (location: HealthSystemDomainLocationsResultLocationViewModel | null) => void;
  showLastLocationMessage: boolean;
  /** Disable the room field (e.g. when adding a pen to an already-selected room). */
  lockRoom?: boolean;
  /** Disable the stable field (e.g. when adding a pen to an already-selected room). */
  lockStable?: boolean;
  /** Optional info banner shown at the top of the dialog. */
  infoText?: string;
  /** Optional override for the dialog title. */
  title?: string;
}

const UpdateLocationModal: React.FC<any> = (props: LocationUpdateModalProps) => {
  const { location, opened, onLocationUpdated, showLastLocationMessage, lockRoom, lockStable, infoText, title } = props;

  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const [animalType, setAnimalType] = React.useState(location.animalType || AnimalType.Unknown);
  const [locationPosition, setLocationPosition] = React.useState(location.positioning || LocationPosition.Left);
  const [locationStable, setLocationStable] = React.useState(location.stable || LocationStable.Unknown);
  const [isSoftDeleted, setIsSoftDeleted] = React.useState(location.isSoftDeleted);
  const [locationArea, setLocationArea] = React.useState(location.area);

  React.useEffect(() => {
    setAnimalType(location.animalType);
    setLocationPosition(location.positioning);
    setLocationStable(location.stable);
    setIsSoftDeleted(location.isSoftDeleted);
    setLocationArea(location.area);
  }, [location]);

  const closeUpdateAnimalDialog = (text: string | null) => {
    if (text == null) {
      onLocationUpdated(null);
      return;
    }
    if (!validateForm()) {
      return;
    }
    onLocationUpdated(location);
  };

  const validateForm = (): boolean => {
    if (!location.number) {
      notifications.warning(t('f-val-chip'));
      return false;
    }

    if (!location.positioning) {
      location.positioning = 10;
    }

    return true;
  };

  const editMode = location.id ? true : false;
  const dialogTitle = title ?? (editMode ? t('f-loc-upd-title') : t('f-loc-create'));

  return (
    <Dialog
      id="updateLocationDialog"
      open={opened}
      onClose={() => closeUpdateAnimalDialog(null)}
      maxWidth="md"
      sx={V4_DIALOG_MD}
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="form-dialog-title"
      slotProps={{ paper: { style: { maxHeight: 'calc(100% - 16px)', margin: 8 } } }}
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <div className="flex w-full justify-between">
              <div className="flex items-center">
                <Tooltip arrow title={t('f-com-cancel')}>
                  <IconButton
                    edge="start"
                    sx={V4_ICON_BUTTON}
                    color="inherit"
                    onClick={() => closeUpdateAnimalDialog(null)}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="h6">{dialogTitle}</Typography>
              </div>

              <Tooltip title={t('f-com-save')} arrow>
                <IconButton
                  color="inherit"
                  id="saveBtn"
                  sx={V4_ICON_BUTTON}
                  onClick={() => closeUpdateAnimalDialog('ok')}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="updateLocationDialogTitle">{dialogTitle}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container>
          {/*<form noValidate*/}
          {/*			autoComplete="off"*/}
          {/*			onSubmit={(e) => {*/}
          {/*				e.preventDefault();*/}
          {/*				closeUpdateAnimalDialog('ok');*/}
          {/*			}}>*/}

          {/* display info text when adding next location */}
          {showLastLocationMessage && (
            <Grid item xs={12}>
              <Alert severity="info">{t('f-loc-upd-pre-alert')}</Alert>
            </Grid>
          )}

          {infoText && (
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="textSecondary"
                component="p"
                className="flex items-start gap-1 leading-snug"
              >
                <InfoOutlinedIcon style={{ fontSize: 16 }} className="mt-0.5 flex-shrink-0" />
                <span>{infoText}</span>
              </Typography>
            </Grid>
          )}

          <Grid item container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('f-chip-number')}
                variant="outlined"
                multiline
                fullWidth
                defaultValue={location.number}
                size="small"
                onChange={(e) => {
                  location.number = e.target.value;
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('f-loc-room')}
                variant="outlined"
                multiline
                fullWidth
                disabled={lockRoom}
                defaultValue={location.room}
                size="small"
                onChange={(e) => {
                  location.room = e.target.value;
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('f-loc-pen')}
                variant="outlined"
                multiline
                fullWidth
                defaultValue={location.pen}
                size="small"
                onChange={(e) => {
                  location.pen = e.target.value;
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocationPositionSelect
                selectId={'locationPositionSelectModal'}
                value={locationPosition || 10}
                fullWidth
                onLocationPositionChange={(value) => {
                  setLocationPosition(value);
                  location.positioning = value;
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <LocationStableSelect
                selectId={'locationStableSelectModal'}
                value={locationStable || 0}
                allowAny={true}
                fullWidth
                disabled={lockStable}
                onLocationStableChange={(value) => {
                  setLocationStable(value);
                  location.stable = value;
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <AnimalTypeSelect
                value={animalType || 0}
                allowAny={true}
                fullWidth
                onAnimalTypeChange={(type) => {
                  setAnimalType(type);
                  location.animalType = type;
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <TextField
                label={t('f-loc-area') + ' ' + t('f-loc-area-unit')}
                variant="outlined"
                multiline
                fullWidth
                defaultValue={locationArea?.areaInM || 0}
                size="small"
                onChange={(e) => {
                  if (!location.area) {
                    location.area = { areaInM: 0 };
                  }

                  location.area.areaInM = Number(e.target.value);
                  setLocationArea(location.area);
                }}
              />
            </Grid>
            {editMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      checkedIcon={<DeleteIcon color="primary" />}
                      checked={isSoftDeleted}
                      onChange={(e) => {
                        setIsSoftDeleted(e.target.checked);
                        location.isSoftDeleted = e.target.checked;
                      }}
                    />
                  }
                  label={t('f-loc-upd-del')}
                />
              </Grid>
            )}
          </Grid>
          {/*</form>*/}
        </Grid>
      </DialogContent>

      {!fullScreen && (
        <DialogActions>
          <Button
            onClick={() => closeUpdateAnimalDialog(null)}
            variant="outlined"
            startIcon={<Icon>{'close'}</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={() => closeUpdateAnimalDialog('ok')}
            id="saveLocationBtn"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UpdateLocationModal;
